import React, { useMemo } from "react";
import {
  useOutletContext,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import NavbarScroll from "../../components/subComponents/NavbarScroll";
import { useItemCategoryInfinite } from "../../hooks/useItemCategoryInfinite";
import ConnectCard2 from "../../components/subComponents/ConnectCard2";
import { saffronRout, saffronURL } from "../../Url";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) =>
  safeArray(arr).map((item, i) => ({ ...item, position: i }));

// ✅ Keep filters in URL while navigating
const keepKeys = [
  "country",
  "state",
  "city",
  "language",
  "category",
  "category_name",
];

const buildSafeSearch = (searchParams, sectionValue) => {
  const current = new URLSearchParams(searchParams);
  const next = new URLSearchParams();

  keepKeys.forEach((k) => {
    const v = current.get(k);
    if (v) next.set(k, v);
  });

  //✅ Prev/Next list building in details page
  next.set("section", sectionValue);

  return `?${next.toString()}`;
};

const Teams = () => {
  const {
    selectedCountry,
    selectedCategory,
    selectedLanguage,
    selectedState,
    selectedCity,
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const categoryName = searchParams.get("category_name") || "";

  // ✅ Optional featured filter (query param based)
  // /profile?featured=1
  const isFeatured = searchParams.get("featured") === "1";

  const {
    item: team,
    ref,
    hasNextPage,
    isFetchingNextPage,
    isPending: teamPending,
  } = useItemCategoryInfinite({
    key: "connect_team",
    endpoint: "teams",
    listKey: "teams", // ✅ API returns data.people
    category_name: categoryName,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    selectedState,
    selectedCity,
    enabled: true,
    threshold: 0.7,

    // ✅ if your hook supports featured param, pass it
    // featured: isFeatured ? 1 : undefined,
  });

  const hasTeam = (team?.length || 0) > 0;

  // ✅ Menu
  const { data: menuData, isLoading: loadingTree } = useQuery({
    queryKey: [
      "menuData_connect_slide",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    staleTime: 1000 * 60 * 10,
    retry: 2,
  });

  const navbaritems = useMemo(() => {
    return menuData?.data?.items?.find((item) => item.key === "team");
  }, [menuData]);

  const listWithPos = useMemo(() => addPosition(team), [team]);

  // ✅ Category change (same as Features)
  const handleCategorySelect = (child) => {
    const params = new URLSearchParams(searchParams);

    if (child?.code) params.set("category_name", child.code);
    else params.delete("category_name");

    // ✅ keep featured in URL (if you use it)
    if (isFeatured) params.set("featured", "1");

    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  // ✅ Navigate to details (same as Features)
  const handleOpenDetails = (item) => {
    const slug = item?.slug || item?.id;
    if (!slug) return;

    // ✅ Simple: section = "people"
    // (If you want featured vs normal separate prev/next, use:
    // section = isFeatured ? "featuredProfiles" : "people"
    const sectionValue = "team";

    navigate(
      {
        pathname: `${saffronRout}/details/team/${slug}`,
        search: buildSafeSearch(searchParams, sectionValue),
      },
      {
        state: {
          sectionKey: sectionValue,
          position: item.position ?? 0,
        },
      },
    );

    window.scrollTo(0, 0);
  };

  return (
    <>
      <NavbarScroll
        items={navbaritems}
        activeCode={categoryName}
        onSelect={handleCategorySelect}
        loading={loadingTree}
      />
      <section className="py-3 px-1 md:px-5 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {listWithPos?.map((item) => (
            <ConnectCard2
              key={item.id}
              data={item}
              onArticleClick={() => handleOpenDetails(item)}
            />
          ))}
        </div>

        {!teamPending && !hasTeam && (
          <p className="text-gray-400 italic mt-4 text-center">
            No profiles found in <span className="font-medium">'Teams'</span>
          </p>
        )}

        {/* ✅ Infinite Scroll trigger */}
        {hasNextPage && <div ref={ref} className="h-10" />}

        {isFetchingNextPage && (
          <div className="text-center text-sm text-gray-500 py-4">
            Loading more...
          </div>
        )}
      </section>
    </>
  );
};

export default Teams;
